import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Badge,
  IconButton,
  useMediaQuery,
  useTheme,
  Paper,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { CONTEXTS } from 'figshelf-modules';
import Icon from './Icon';
import { useAuthModal } from '../contexts/AuthModalContext';
import FloatingMenu from './FloatingMenu';

const MobileBottomNav = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(CONTEXTS.AuthContext);
  const { openLogin } = useAuthModal();
  const { totalUnreadCount } = CONTEXTS.useMessages();
  const { unreadCount } = CONTEXTS.useNotifications();
  const [floatingMenuAnchorEl, setFloatingMenuAnchorEl] = useState(null);

  if (!isMobile) return null;

  const hiddenPaths = ['/settings', '/profile/edit', '/profile/views', '/profile/details', '/profile-setup', '/activity', '/privacy-policy'];
  const isHiddenPath = hiddenPaths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));
  if (isHiddenPath) return null;

  const isGuest = !auth?.isSignedIn;

  const getUsername = () => {
    try {
      const cached = localStorage.getItem('currentUser');
      if (cached) return JSON.parse(cached)?.username || 'me';
    } catch {}
    return 'me';
  };

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const navItems = [
    { id: 'home', icon: 'home', path: '/' },
    { id: 'database', icon: 'database', path: '/database' },
    { id: 'messages', icon: 'messages', path: isGuest ? null : '/messages', badge: totalUnreadCount },
    { id: 'profile', icon: 'profile', path: isGuest ? null : `/user/${getUsername()}` },
  ];

  const handleNav = (path) => {
    if (!path) { openLogin(); return; }
    navigate(path);
  };

  const handleFloatingMenuOptionPress = (option) => {
    setFloatingMenuAnchorEl(null);
    switch (option.id) {
      case 'post': navigate('/', { state: { openPostCreator: true } }); break;
      case 'figure': navigate('/create-figure'); break;
      case 'company': navigate('/create-company'); break;
      case 'group': navigate('/create-group'); break;
      default: break;
    }
  };

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1300,
          borderTop: '1px solid',
          borderColor: 'divider',
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', height: 60, px: 1 }}>
          {navItems.slice(0, 2).map((item) => (
            <IconButton
              key={item.id}
              onClick={() => handleNav(item.path)}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1,
                color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                flexDirection: 'column',
                gap: 0.25,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Icon name={item.icon} color={isActive(item.path) ? theme.palette.primary.main : theme.palette.text.secondary} size={24} />
            </IconButton>
          ))}

          {/* FAB Add Button */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <IconButton
              onClick={(e) => setFloatingMenuAnchorEl(e.currentTarget)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 46,
                height: 46,
                '&:hover': { bgcolor: 'primary.dark' },
                boxShadow: 3,
              }}
            >
              <Add />
            </IconButton>
          </Box>

          {navItems.slice(2).map((item) => (
            <IconButton
              key={item.id}
              onClick={() => handleNav(item.path)}
              sx={{
                flex: 1,
                borderRadius: 2,
                py: 1,
                color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Badge badgeContent={item.badge || 0} color="error" invisible={!item.badge}>
                <Icon name={item.icon} color={isActive(item.path) ? theme.palette.primary.main : theme.palette.text.secondary} size={24} />
              </Badge>
            </IconButton>
          ))}
        </Box>
      </Paper>

      <FloatingMenu
        anchorEl={floatingMenuAnchorEl}
        open={Boolean(floatingMenuAnchorEl)}
        onClose={() => setFloatingMenuAnchorEl(null)}
        onOptionPress={handleFloatingMenuOptionPress}
      />
    </>
  );
};

export default MobileBottomNav;
